import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SUPPORTED_LANGUAGES = ['es', 'fr', 'de', 'it', 'tr', 'ar', 'bn']; // excluding 'en' as it's the default (no prefix)
const ALL_LANGUAGES = ['en', ...SUPPORTED_LANGUAGES];

// Routes that have translated equivalents (i.e. /ar/blog)
const PUBLIC_PREFIXES = [
  '/login', '/signup', '/verify-email', '/forgot-password', '/reset-password', '/accept-invite',
  '/privacy-policy', '/terms-of-service', '/cookie-policy', '/faq', '/blog', '/features', 
  '/walkthroughs', '/r/', '/about', '/security'
];

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
}

export default function LanguageRedirector() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    // 4. RUN ONCE - guard so it fires a single time per page load
    if (hasRun.current) return;
    hasRun.current = true;

    const cookieLang = getCookie('i18next');
    if (!cookieLang || !ALL_LANGUAGES.includes(cookieLang)) return;

    const path = location.pathname;

    // Check if current URL already has a locale prefix
    const pathSegments = path.split('/').filter(Boolean);
    const hasLocalePrefix = SUPPORTED_LANGUAGES.includes(pathSegments[0]);

    // 2. PATH WINS OVER COOKIE - if the user explicitly opened a locale-prefixed URL, do NOT redirect.
    // i18next's LanguageRouteWrapper will update the language to match the path, 
    // and since caches:['cookie'] is set, the cookie will automatically be updated.
    if (hasLocalePrefix) return;

    // The URL has NO locale prefix (default English route).
    // If the cookie says another language, we should apply it.
    if (cookieLang !== 'en') {
      const isPublicRoute = path === '/' || PUBLIC_PREFIXES.some(prefix => path.startsWith(prefix));
      
      if (isPublicRoute) {
        // 1. GENERIC & 3. PRESERVE THE FULL PATH - Redirect to the equivalent path in the saved language
        // 5. Use replace-style navigation
        const newPath = `/${cookieLang}${path === '/' ? '' : path}${location.search}${location.hash}`;
        navigate(newPath, { replace: true });
      } else {
        // 3. If target translated route doesn't exist (e.g. /dashboard), stay put.
        // But we MUST still apply the user's saved language so the dashboard isn't stuck in English!
        if (i18n.resolvedLanguage !== cookieLang) {
          i18n.changeLanguage(cookieLang);
        }
      }
    }
  }, [location, navigate, i18n]);

  return null;
}
